import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import Administrator from "../models/Administrator";
import EventBus from "../utils/EventBus";
import MaterialIcons from "../icons/MaterialIcons";
import TextView from "./TextView";
import UserType from "../classes/UserType";
import IconView from "./IconView";
import NOKUser from "../models/NOKUser";
import NotificatonViewTabChange from "../classes/NotificatonViewTabChange";
import AdministratorType from "../classes/AdministratorType";
import { Subscription } from "rxjs";
import { BuildContext } from "../classes/BuildContext";

type _SideMenuViewOnMenuChanged = (menu: string) => void;

type _RequestsButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _DashboardButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _StaffButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _HospitalButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _SupportUserButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _InvoiceButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

type _SettingsButtonOnPressed = (d3eState: SideMenuViewRefs) => void;

export interface SideMenuViewProps extends BaseUIProps {
  key?: string;
  administrator?: Administrator;
  user?: NOKUser;
  onMenuChanged: _SideMenuViewOnMenuChanged;
}
/// To store state data for SideMenuView
class SideMenuViewRefs {
  public dashboardButton: DashboardButtonState = new DashboardButtonState();
  public hospitalButton: HospitalButtonState = new HospitalButtonState();
  public invoiceButton: InvoiceButtonState = new InvoiceButtonState();
  public requestsButton: RequestsButtonState = new RequestsButtonState();
  public settingsButton: SettingsButtonState = new SettingsButtonState();
  public staffButton: StaffButtonState = new StaffButtonState();
  public supportUserButton: SupportUserButtonState =
    new SupportUserButtonState();
}

interface SettingsButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onSettingsButtonHandler?: _SettingsButtonOnPressed;
  selectedMenu: string;
}

class SettingsButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _SettingsButtonWithState extends ObservableComponent<SettingsButtonWithStateProps> {
  settingsButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SettingsButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get settingsButton(): SettingsButtonState {
    return this.props.d3eState.settingsButton;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onSettingsButtonHandler(): _SettingsButtonOnPressed {
    return this.props._onSettingsButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("settingsButton", null, this.settingsButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["selectedMenu", "settingsButton", "settingsButton.hover"],
      this.rebuild
    );
  }
  public settingsButtonOnEnter(event): void {
    return this.settingsButton.setHover(true);
  }
  public settingsButtonOnExit(event): void {
    return this.settingsButton.setHover(false);
  }
  public dispose(): void {
    this.settingsButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: ui.EdgeInsets.all(7.0, new Map()),
        decoration:
          this.selectedMenu == "Settings"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.settingsButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        disable: this.settingsButton.disable,
        onPressed: () => {
          this._onSettingsButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.settings,
              size: 24,
              color:
                this.selectedMenu == "Settings"
                  ? cStyle.c19
                  : this.settingsButton.hover
                  ? cStyle.c19
                  : cStyle.c21,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Settings",
                  "data-c1": this.settingsButton.hover,
                },
                {}
              ),
              className: "xc6c",
              key: "0",
            }),
            TextView({
              data: "Settings",
              softWrap: true,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Settings",
                  "data-c1": this.settingsButton.hover,
                },
                {}
              ),
              style: new ui.TextStyle({
                color:
                  this.selectedMenu == "Settings"
                    ? cStyle.c19
                    : this.settingsButton.hover
                    ? cStyle.c19
                    : cStyle.c21,
              }),
              className: "x381",
              key: "1",
            }),
          ],
          className: "x02d hc h",
        }),
        onEnter: (event) => {
          this.settingsButtonOnEnter(event);
        },
        onExit: (event) => {
          this.settingsButtonOnExit(event);
        },
      }),
      className: "x50f8 hc h",
    });
  }
}
function SettingsButtonWithState(props: SettingsButtonWithStateProps) {
  return React.createElement(_SettingsButtonWithState, props);
}

interface InvoiceButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onInvoiceButtonHandler?: _InvoiceButtonOnPressed;
  selectedMenu: string;
}

class InvoiceButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _InvoiceButtonWithState extends ObservableComponent<InvoiceButtonWithStateProps> {
  invoiceButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: InvoiceButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get invoiceButton(): InvoiceButtonState {
    return this.props.d3eState.invoiceButton;
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onInvoiceButtonHandler(): _InvoiceButtonOnPressed {
    return this.props._onInvoiceButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("invoiceButton", null, this.invoiceButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["invoiceButton", "invoiceButton.hover", "selectedMenu"],
      this.rebuild
    );
  }
  public invoiceButtonOnEnter(event): void {
    return this.invoiceButton.setHover(true);
  }
  public invoiceButtonOnExit(event): void {
    return this.invoiceButton.setHover(false);
  }
  public dispose(): void {
    this.invoiceButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: ui.EdgeInsets.all(7.0, new Map()),
        decoration:
          this.selectedMenu == "Invoices"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.invoiceButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        disable: this.invoiceButton.disable,
        onPressed: () => {
          this._onInvoiceButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            ui.Wrap({
              crossAxisAlignment: ui.WrapCrossAlignment.center,
              children: [
                IconView({
                  icon: MaterialIcons.receipt_long,
                  size: 24,
                  color:
                    this.selectedMenu == "Invoices"
                      ? cStyle.c19
                      : this.invoiceButton.hover
                      ? cStyle.c19
                      : cStyle.c21,
                  states: ui.joinStates(
                    {
                      "data-c0": this.selectedMenu == "Invoices",
                      "data-c1": this.invoiceButton.hover,
                    },
                    {}
                  ),
                  className: "x40a",
                  key: "0",
                }),
                TextView({
                  softWrap: true,
                  data: "Invoices",
                  states: ui.joinStates(
                    {
                      "data-c0": this.selectedMenu == "Invoices",
                      "data-c1": this.invoiceButton.hover,
                    },
                    {}
                  ),
                  style: new ui.TextStyle({
                    color:
                      this.selectedMenu == "Invoices"
                        ? cStyle.c19
                        : this.invoiceButton.hover
                        ? cStyle.c19
                        : cStyle.c21,
                  }),
                  className: "x145",
                  key: "1",
                }),
              ],
              key: "0",
            }),
          ],
          className: "x851 hc h",
        }),
        onEnter: (event) => {
          this.invoiceButtonOnEnter(event);
        },
        onExit: (event) => {
          this.invoiceButtonOnExit(event);
        },
      }),
      className: "xa6 hc h",
    });
  }
}
function InvoiceButtonWithState(props: InvoiceButtonWithStateProps) {
  return React.createElement(_InvoiceButtonWithState, props);
}

interface SupportUserButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onsupportUserButtonHandler?: _SupportUserButtonOnPressed;
  selectedMenu: string;
}

class SupportUserButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _SupportUserButtonWithState extends ObservableComponent<SupportUserButtonWithStateProps> {
  supportUserButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SupportUserButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get supportUserButton(): SupportUserButtonState {
    return this.props.d3eState.supportUserButton;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onsupportUserButtonHandler(): _SupportUserButtonOnPressed {
    return this.props._onsupportUserButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("supportUserButton", null, this.supportUserButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["selectedMenu", "supportUserButton", "supportUserButton.hover"],
      this.rebuild
    );
  }
  public supportUserButtonOnEnter(event): void {
    return this.supportUserButton.setHover(true);
  }
  public supportUserButtonOnExit(event): void {
    return this.supportUserButton.setHover(false);
  }
  public dispose(): void {
    this.supportUserButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: ui.EdgeInsets.all(7.0, new Map()),
        decoration:
          this.selectedMenu == "SupportUser"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.supportUserButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        disable: this.supportUserButton.disable,
        onPressed: () => {
          this._onsupportUserButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        states: ui.joinStates({ "data-visibility": false }, {}),
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.support_agent,
              size: 24,
              color:
                this.selectedMenu == "SupportUser"
                  ? cStyle.c19
                  : this.supportUserButton.hover
                  ? cStyle.c19
                  : cStyle.c21,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "SupportUser",
                  "data-c1": this.supportUserButton.hover,
                },
                {}
              ),
              className: "x974",
              key: "0",
            }),
            TextView({
              data: "Support Users",
              softWrap: true,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "SupportUser",
                  "data-c1": this.supportUserButton.hover,
                },
                {}
              ),
              style: new ui.TextStyle({
                color:
                  this.selectedMenu == "SupportUser"
                    ? cStyle.c19
                    : this.supportUserButton.hover
                    ? cStyle.c19
                    : cStyle.c21,
              }),
              className: "x344",
              key: "1",
            }),
          ],
          className: "x14e hc h",
        }),
        onEnter: (event) => {
          this.supportUserButtonOnEnter(event);
        },
        onExit: (event) => {
          this.supportUserButtonOnExit(event);
        },
      }),
      className: "xf9a hc h",
      states: ui.joinStates({ "data-visibility": false }, {}),
    });
  }
}
function SupportUserButtonWithState(props: SupportUserButtonWithStateProps) {
  return React.createElement(_SupportUserButtonWithState, props);
}

interface HospitalButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onHospitalButtonHandler?: _HospitalButtonOnPressed;
  selectedMenu: string;
}

class HospitalButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _HospitalButtonWithState extends ObservableComponent<HospitalButtonWithStateProps> {
  hospitalButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: HospitalButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get hospitalButton(): HospitalButtonState {
    return this.props.d3eState.hospitalButton;
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onHospitalButtonHandler(): _HospitalButtonOnPressed {
    return this.props._onHospitalButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("hospitalButton", null, this.hospitalButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["hospitalButton", "hospitalButton.hover", "selectedMenu"],
      this.rebuild
    );
  }
  public hospitalButtonOnEnter(event): void {
    return this.hospitalButton.setHover(true);
  }
  public hospitalButtonOnExit(event): void {
    return this.hospitalButton.setHover(false);
  }
  public dispose(): void {
    this.hospitalButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: ui.EdgeInsets.all(7.0, new Map()),
        decoration:
          this.selectedMenu == "Hospital"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.hospitalButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        disable: this.hospitalButton.disable,
        onPressed: () => {
          this._onHospitalButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.health_and_safety,
              size: 24,
              color:
                this.selectedMenu == "Hospital"
                  ? cStyle.c19
                  : this.hospitalButton.hover
                  ? cStyle.c19
                  : cStyle.c21,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Hospital",
                  "data-c1": this.hospitalButton.hover,
                },
                {}
              ),
              className: "x0f0c",
              key: "0",
            }),
            TextView({
              data: "Hospitals",
              softWrap: true,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Hospital",
                  "data-c1": this.hospitalButton.hover,
                },
                {}
              ),
              style: new ui.TextStyle({
                color:
                  this.selectedMenu == "Hospital"
                    ? cStyle.c19
                    : this.hospitalButton.hover
                    ? cStyle.c19
                    : cStyle.c21,
              }),
              className: "xecf",
              key: "1",
            }),
          ],
          className: "x764 hc h",
        }),
        onEnter: (event) => {
          this.hospitalButtonOnEnter(event);
        },
        onExit: (event) => {
          this.hospitalButtonOnExit(event);
        },
      }),
      className: "x14 hc h",
    });
  }
}
function HospitalButtonWithState(props: HospitalButtonWithStateProps) {
  return React.createElement(_HospitalButtonWithState, props);
}

interface StaffButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onStaffButtonHandler?: _StaffButtonOnPressed;
  selectedMenu: string;
}

class StaffButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _StaffButtonWithState extends ObservableComponent<StaffButtonWithStateProps> {
  staffButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: StaffButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get staffButton(): StaffButtonState {
    return this.props.d3eState.staffButton;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onStaffButtonHandler(): _StaffButtonOnPressed {
    return this.props._onStaffButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("staffButton", null, this.staffButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["selectedMenu", "staffButton", "staffButton.hover"], this.rebuild);
  }
  public staffButtonOnEnter(event): void {
    return this.staffButton.setHover(true);
  }
  public staffButtonOnExit(event): void {
    return this.staffButton.setHover(false);
  }
  public dispose(): void {
    this.staffButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        padding: ui.EdgeInsets.all(7.0, new Map()),
        decoration:
          this.selectedMenu == "Staff"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.staffButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        disable: this.staffButton.disable,
        onPressed: () => {
          this._onStaffButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.people_alt,
              size: 24,
              color:
                this.selectedMenu == "Staff"
                  ? cStyle.c19
                  : this.staffButton.hover
                  ? cStyle.c19
                  : cStyle.c21,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Staff",
                  "data-c1": this.staffButton.hover,
                },
                {}
              ),
              className: "x684",
              key: "0",
            }),
            TextView({
              data: "Staff",
              softWrap: true,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Staff",
                  "data-c1": this.staffButton.hover,
                },
                {}
              ),
              style: new ui.TextStyle({
                color:
                  this.selectedMenu == "Staff"
                    ? cStyle.c19
                    : this.staffButton.hover
                    ? cStyle.c19
                    : cStyle.c21,
              }),
              className: "xf66",
              key: "1",
            }),
          ],
          className: "x884 hc h",
        }),
        onEnter: (event) => {
          this.staffButtonOnEnter(event);
        },
        onExit: (event) => {
          this.staffButtonOnExit(event);
        },
      }),
      className: "xc91 hc h",
    });
  }
}
function StaffButtonWithState(props: StaffButtonWithStateProps) {
  return React.createElement(_StaffButtonWithState, props);
}

interface DashboardButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onDashboardButtonHandler?: _DashboardButtonOnPressed;
  selectedMenu: string;
}

class DashboardButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _DashboardButtonWithState extends ObservableComponent<DashboardButtonWithStateProps> {
  dashboardButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: DashboardButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get dashboardButton(): DashboardButtonState {
    return this.props.d3eState.dashboardButton;
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onDashboardButtonHandler(): _DashboardButtonOnPressed {
    return this.props._onDashboardButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("dashboardButton", null, this.dashboardButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["dashboardButton", "dashboardButton.hover", "selectedMenu"],
      this.rebuild
    );
  }
  public dashboardButtonOnEnter(event): void {
    return this.dashboardButton.setHover(true);
  }
  public dashboardButtonOnExit(event): void {
    return this.dashboardButton.setHover(false);
  }
  public dispose(): void {
    this.dashboardButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 10.0, new Map()),
      child: Button({
        decoration:
          this.selectedMenu == "Dashboard"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.dashboardButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        padding: ui.EdgeInsets.all(7.0, new Map()),
        disable: this.dashboardButton.disable,
        onPressed: () => {
          this._onDashboardButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        states: ui.joinStates({ "data-visibility": false }, {}),
        child: ui.Row({
          children: [
            IconView({
              icon: MaterialIcons.dashboard,
              size: 24,
              color:
                this.selectedMenu == "Dashboard"
                  ? cStyle.c19
                  : this.dashboardButton.hover
                  ? cStyle.c19
                  : cStyle.c21,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Dashboard",
                  "data-c1": this.dashboardButton.hover,
                },
                {}
              ),
              className: "x0e3",
              key: "0",
            }),
            TextView({
              data: "Dashboard",
              softWrap: true,
              states: ui.joinStates(
                {
                  "data-c0": this.selectedMenu == "Dashboard",
                  "data-c1": this.dashboardButton.hover,
                },
                {}
              ),
              style: new ui.TextStyle({
                color:
                  this.selectedMenu == "Dashboard"
                    ? cStyle.c19
                    : this.dashboardButton.hover
                    ? cStyle.c19
                    : cStyle.c21,
              }),
              className: "x2b0",
              key: "1",
            }),
          ],
          className: "xcf9 hc h",
        }),
        onEnter: (event) => {
          this.dashboardButtonOnEnter(event);
        },
        onExit: (event) => {
          this.dashboardButtonOnExit(event);
        },
      }),
      className: "xde hc h",
      states: ui.joinStates({ "data-visibility": false }, {}),
    });
  }
}
function DashboardButtonWithState(props: DashboardButtonWithStateProps) {
  return React.createElement(_DashboardButtonWithState, props);
}

interface RequestsButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: SideMenuViewRefs;
  _onFacilitieButtonHandler?: _RequestsButtonOnPressed;
  selectedMenu: string;
}

class RequestsButtonState extends ObjectObservable {
  private _hover: boolean = false;
  private _disable: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
}

class _RequestsButtonWithState extends ObservableComponent<RequestsButtonWithStateProps> {
  requestsButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: RequestsButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get requestsButton(): RequestsButtonState {
    return this.props.d3eState.requestsButton;
  }
  public get selectedMenu(): string {
    return this.props.selectedMenu;
  }
  public get d3eState(): SideMenuViewRefs {
    return this.props.d3eState;
  }
  public get _onFacilitieButtonHandler(): _RequestsButtonOnPressed {
    return this.props._onFacilitieButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("requestsButton", null, this.requestsButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["requestsButton", "requestsButton.hover", "selectedMenu"],
      this.rebuild
    );
  }
  public requestsButtonOnEnter(event): void {
    return this.requestsButton.setHover(true);
  }
  public requestsButtonOnExit(event): void {
    return this.requestsButton.setHover(false);
  }
  public dispose(): void {
    this.requestsButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      margin: ui.EdgeInsets.symmetric({
        horizontal: 0.0,
        vertical: 10.0,
        transitions: new Map(),
      }),
      child: Button({
        decoration:
          this.selectedMenu == "Requests"
            ? new ui.BoxDecoration({
                color: cStyle.c21,
                border: ui.Border.all({ color: cStyle.c21, width: 0.0 }),
              })
            : this.requestsButton.hover
            ? new ui.BoxDecoration({ color: cStyle.c21 })
            : new ui.BoxDecoration({
                color: cStyle.c22,
                border: ui.Border.all({ color: cStyle.c22, width: 0.0 }),
              }),
        padding: ui.EdgeInsets.all(7.0, new Map()),
        disable: this.requestsButton.disable,
        onPressed: () => {
          this._onFacilitieButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            ui.Wrap({
              crossAxisAlignment: ui.WrapCrossAlignment.center,
              children: [
                IconView({
                  icon: MaterialIcons.menu_book,
                  size: 24,
                  color:
                    this.selectedMenu == "Requests"
                      ? cStyle.c19
                      : this.requestsButton.hover
                      ? cStyle.c19
                      : cStyle.c21,
                  states: ui.joinStates(
                    {
                      "data-c0": this.selectedMenu == "Requests",
                      "data-c1": this.requestsButton.hover,
                    },
                    {}
                  ),
                  className: "x556",
                  key: "0",
                }),
                TextView({
                  softWrap: true,
                  data: "Requests",
                  states: ui.joinStates(
                    {
                      "data-c0": this.selectedMenu == "Requests",
                      "data-c1": this.requestsButton.hover,
                    },
                    {}
                  ),
                  style: new ui.TextStyle({
                    color:
                      this.selectedMenu == "Requests"
                        ? cStyle.c19
                        : this.requestsButton.hover
                        ? cStyle.c19
                        : cStyle.c21,
                  }),
                  className: "x34a6",
                  key: "1",
                }),
              ],
              key: "0",
            }),
          ],
          className: "x0c5 hc h",
        }),
        onEnter: (event) => {
          this.requestsButtonOnEnter(event);
        },
        onExit: (event) => {
          this.requestsButtonOnExit(event);
        },
      }),
      className: "x64f hc h",
    });
  }
}
function RequestsButtonWithState(props: RequestsButtonWithStateProps) {
  return React.createElement(_RequestsButtonWithState, props);
}

class _SideMenuViewState extends ObservableComponent<SideMenuViewProps> {
  static defaultProps = {
    administrator: null,
    user: null,
    onMenuChanged: null,
  };
  d3eState: SideMenuViewRefs = new SideMenuViewRefs();
  private _notificatonViewTabChangeSubscription: Subscription;
  selectedMenu: string = "Requests";
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SideMenuViewProps) {
    super(props);

    this.initState();
  }
  public get administrator(): Administrator {
    return this.props.administrator;
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this._notificatonViewTabChangeSubscription = EventBus.get()
      .on<NotificatonViewTabChange>(NotificatonViewTabChange)
      .subscribe((value) => this.onNotificatonViewTabChange(value));
  }
  public initListeners(): void {
    this.updateSyncProperty("administrator", this.props.administrator);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "administrator",
        "administrator.administratorType",
        "selectedMenu",
        "user",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: SideMenuViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.administrator !== this.props.administrator) {
      this.updateObservable(
        "administrator",
        prevProps.administrator,
        this.props.administrator
      );

      this.fire("administrator", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setSelectedMenu(val: string): void {
    let isValChanged: boolean = this.selectedMenu !== val;

    if (!isValChanged) {
      return;
    }

    this.selectedMenu = val;

    this.fire("selectedMenu", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.center,
      mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
      children: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.AssetImage({
              path: "images/footer.png",
              width: 200,
              height: 76,
              className: "x232 hc vc",
              key: "0",
            }),
            RequestsButtonWithState({
              d3eState: this.d3eState,
              _onFacilitieButtonHandler: this.onFacilitieButtonHandler,
              selectedMenu: this.selectedMenu,
              key: "1",
            }),
            DashboardButtonWithState({
              d3eState: this.d3eState,
              _onDashboardButtonHandler: this.onDashboardButtonHandler,
              selectedMenu: this.selectedMenu,
              key: "2",
            }),
            this.user !== null && this.user.userType === UserType.Admin
              ? StaffButtonWithState({
                  d3eState: this.d3eState,
                  _onStaffButtonHandler: this.onStaffButtonHandler,
                  selectedMenu: this.selectedMenu,
                })
              : [],
            this.administrator !== null &&
            this.administrator.administratorType === AdministratorType.Admin
              ? HospitalButtonWithState({
                  d3eState: this.d3eState,
                  _onHospitalButtonHandler: this.onHospitalButtonHandler,
                  selectedMenu: this.selectedMenu,
                })
              : [],
            this.administrator !== null &&
            this.administrator.administratorType === AdministratorType.Admin
              ? SupportUserButtonWithState({
                  d3eState: this.d3eState,
                  _onsupportUserButtonHandler: this.onsupportUserButtonHandler,
                  selectedMenu: this.selectedMenu,
                })
              : [],
            (this.administrator !== null &&
              this.administrator.administratorType ===
                AdministratorType.Admin) ||
            (this.user !== null && this.user.userType === UserType.Admin)
              ? InvoiceButtonWithState({
                  d3eState: this.d3eState,
                  _onInvoiceButtonHandler: this.onInvoiceButtonHandler,
                  selectedMenu: this.selectedMenu,
                })
              : [],
            SettingsButtonWithState({
              d3eState: this.d3eState,
              _onSettingsButtonHandler: this.onSettingsButtonHandler,
              selectedMenu: this.selectedMenu,
              key: "7",
            }),
          ],
          className: "x52e hc h",
          key: "0",
        }),
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.center,
          children: [
            ui.AssetImage({
              path: "images/custom-17 1.png",
              width: 289,
              height: 203,
              className: "xc06 hc vc",
              key: "0",
            }),
          ],
          className: "hc",
          key: "1",
        }),
      ],
      className: ui.join(this.props.className, "SideMenuView x9708 hc vc v"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onDashboardButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Dashboard");

    this.onMenuChanged(this.selectedMenu);
  };
  public onFacilitieButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Requests");

    this.onMenuChanged(this.selectedMenu);

    EventBus.get().fire(
      new NotificatonViewTabChange({ tabChangeType: "Requests" })
    );
  };
  public onSettingsButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Settings");

    this.onMenuChanged(this.selectedMenu);
  };
  public onInvoiceButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Invoices");

    this.onMenuChanged(this.selectedMenu);

    EventBus.get().fire(
      new NotificatonViewTabChange({ tabChangeType: "Invoices" })
    );
  };
  public onHospitalButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Hospital");

    this.onMenuChanged(this.selectedMenu);
  };
  public onStaffButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("Staff");

    this.onMenuChanged(this.selectedMenu);
  };
  public onsupportUserButtonHandler = (d3eState: SideMenuViewRefs): void => {
    this.setSelectedMenu("SupportUser");

    this.onMenuChanged(this.selectedMenu);
  };
  public onNotificatonViewTabChange = (tab: NotificatonViewTabChange): void => {
    this.setSelectedMenu(tab.tabChangeType);
  };
  public get onMenuChanged(): _SideMenuViewOnMenuChanged {
    return this.props.onMenuChanged;
  }
  public dispose(): void {
    this._notificatonViewTabChangeSubscription.unsubscribe();

    super.dispose();
  }
  public get dashboardButton() {
    return this.d3eState.dashboardButton;
  }
  public get hospitalButton() {
    return this.d3eState.hospitalButton;
  }
  public get invoiceButton() {
    return this.d3eState.invoiceButton;
  }
  public get requestsButton() {
    return this.d3eState.requestsButton;
  }
  public get settingsButton() {
    return this.d3eState.settingsButton;
  }
  public get staffButton() {
    return this.d3eState.staffButton;
  }
  public get supportUserButton() {
    return this.d3eState.supportUserButton;
  }
}
export default function SideMenuView(props: SideMenuViewProps) {
  return React.createElement(_SideMenuViewState, {
    ..._SideMenuViewState.defaultProps,
    ...props,
  });
}
